import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import { tokens, Text, TextInput, Button, Link } from '@hotjar/hotsauce';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { verifyRecoveryCode } from '@hotjar/webapp/domains/mfa/api';
import { Trans } from 'react-i18next';
import { useMFALogin } from '@hotjar/webapp/features/Mfa/useMFALogin';
import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';

const CONTACT_SUPPORT_LINK = '/requests/new?ticket_form_id=360000390353';

const Container = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x2};
  max-width: 460px;
  margin: 0 auto;
  width: 100%;
`;

const Logo = styled.div`
  width: 200px;
  height: 24px;
  background: url(/static/app/img/logo-hotjar.svg) center center no-repeat transparent;
  background-size: contain;
  margin: 0 auto;
  margin-bottom: ${tokens.spacer.x7};
`;

const StyledDescription = styled(Text)`
  margin-top: ${tokens.spacer.x2};
  margin-bottom: ${tokens.spacer.x6};
`;

const StyledError = styled(Text)`
  display: block;
  margin-top: ${tokens.spacer.x1};
`;

const StyledButton = styled(Button)`
  width: 100%;
  justify-content: center;
  margin-top: ${tokens.spacer.x4};
`;

const StyledRecoveryCodesText = styled(Text)`
  margin-top: ${tokens.spacer.x5};
`;

export const LoginRecoveryCodePage = (): JSX.Element => {
  const { t } = useTranslationWithPrefix('mfa:recovery-code-login');
  const helpHotjarUrl = useHelpHotjarUrl();
  const [isLoading, setIsLoading] = useState(false);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState('');
  const { redirectToLogin, onLogin, mfaStateKey, onClickOtp } = useMFALogin();
  const { setIsAppInterfaceVisible } = useUIState();

  useEffect(() => {
    setIsAppInterfaceVisible(false);
  }, [setIsAppInterfaceVisible]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecoveryCode(e.target.value);
  };

  const handleVerify = () => {
    if (!mfaStateKey) {
      return;
    }

    setIsLoading(true);
    verifyRecoveryCode({ recoveryCode, mfaStateKey })
      .then(() => {
        onLogin();
      })
      .catch((err) => {
        setErrorType(err.response.data.error_type);
        setIsLoading(false);
      });
  };

  return (
    <Container>
      <Logo />
      <Text as="h1" textType="header4">
        {t('title')}
      </Text>
      <StyledDescription as="p" textColor="text_light">
        {t('description')}
      </StyledDescription>
      <TextInput
        onChange={handleChange}
        data-testid="verify-recovery-code-input"
        maxLength={10}
        pattern="[0-9a-f]{10}"
        placeholder="10-character code"
      />
      {!!errorType && (
        <StyledError textColor="text_error">
          {(errorType === 'ValidationError' || errorType === 'Unauthorized') &&
            t('invalid-recovery-code')}
          {errorType === 'LockedUser' && t('locked-user')}
          {errorType === 'InvalidMFAState' && (
            <Trans i18nKey="mfa:recovery-code-login:invalid-mfa-state">
              <Link
                as="button"
                kind="inline"
                data-testid="mfa-restart-login"
                onClick={redirectToLogin}
              >
                restart login
              </Link>
            </Trans>
          )}
        </StyledError>
      )}
      <StyledButton
        kind="primary"
        size="l"
        isLoading={isLoading}
        isDisabled={isLoading}
        onClick={handleVerify}
        type="submit"
      >
        {t('action')}
      </StyledButton>
      <StyledRecoveryCodesText as="p" textColor="text_light">
        <Trans i18nKey="mfa:recovery-code-login:otp-cta">
          <Link as="button" kind="inline" data-testid="mfa-otp-link" onClick={onClickOtp}>
            Enter your OTP code
          </Link>
          .
        </Trans>
      </StyledRecoveryCodesText>
      <StyledRecoveryCodesText as="p" textColor="text_light">
        <Trans i18nKey="mfa:recovery-code-login.help">
          Don&apos;t have your recovery codes?{' '}
          <a
            href={`${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact support
          </a>
          .
        </Trans>
      </StyledRecoveryCodesText>
    </Container>
  );
};
